import React, { useCallback, useEffect, useRef, useState } from "react";
import { Gift, Mail, RotateCcw, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const STATUS_LABEL = { not_sent: "Not sent", sent: "Sent", sending: "Sending", failed: "Failed, can retry", simulated: "Simulated" };
const th = "text-left px-3 py-2 font-semibold uppercase tracking-wider text-xs";

export default function SharingTreeToyFollowups({ year, onClose, onComplete }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmed, setConfirmed] = useState(false);
  const [resend, setResend] = useState(false);
  const [batchSize, setBatchSize] = useState(25);
  const seq = useRef(0);
  const sendingRef = useRef(false);
  const mounted = useRef(true);
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const load = useCallback(async (keepIds) => {
    const s = ++seq.current;
    setLoading(true);
    setLoadError("");
    try {
      const { data: d } = await api.get(`/sharing-tree/toy-followups?year=${year}`);
      if (!mounted.current || s !== seq.current || sendingRef.current) return;
      if (d?.year !== undefined && Number(d.year) !== Number(year)) return;
      setData(d);
      setConfirmed(false);
      setSelected((prev) => {
        const base = keepIds ? new Set(keepIds) : prev;
        const next = new Set();
        (d.rows || []).forEach((r) => { if (base.has(r.household_id) && r.can_contact && r.delivery_status !== "sending") next.add(r.household_id); });
        return next;
      });
    } catch (e) {
      if (mounted.current && s === seq.current) setLoadError(formatApiError(e));
    } finally {
      if (mounted.current && s === seq.current) setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    mounted.current = true;
    load();
    const onFocus = () => { if (!sendingRef.current) load(); };
    window.addEventListener("focus", onFocus);
    return () => { mounted.current = false; seq.current += 1; window.removeEventListener("focus", onFocus); };
  }, [load]);

  useEffect(() => {
    const prev = document.activeElement;
    closeRef.current?.focus();
    function onKey(e) {
      if (e.key === "Escape") { e.preventDefault(); if (!sendingRef.current) onCloseRef.current?.(); return; }
      if (e.key !== "Tab" || !dialogRef.current) return;
      const els = [...dialogRef.current.querySelectorAll("button:not([disabled]),input:not([disabled]),a[href],[tabindex]:not([tabindex='-1'])")];
      if (!els.length) return;
      const first = els[0], last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); prev?.focus?.(); };
  }, []);

  const rows = data?.rows || [];
  const usage = data?.usage;
  const remainingQuota = usage?.daily_remaining ?? 0;
  const blocked = !data?.is_open || !!data?.blocked_reason;
  const selectable = (r) => r.can_contact && r.delivery_status !== "sending" && (resend || (!r.last_sent_at && r.delivery_status !== "sent"));
  const chosen = rows.filter((r) => selected.has(r.household_id) && selectable(r));
  const children = chosen.reduce((n, r) => n + (r.missing_count || 0), 0);
  const maxBatch = Math.max(1, Math.min(100, remainingQuota || 1));
  const canSend = !!data && !loading && !loadError && !sending && !blocked && chosen.length > 0 && remainingQuota > 0 && confirmed;

  function toggle(id) {
    setConfirmed(false);
    setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function selectAll(on) { setConfirmed(false); setSelected(on ? new Set(rows.filter(selectable).map((r) => r.household_id)) : new Set()); }

  async function send() {
    if (!canSend || sendingRef.current) return;
    const size = Math.max(1, Math.min(Number(batchSize) || 1, maxBatch));
    const generations = {};
    chosen.forEach((r) => { generations[r.household_id] = r.generation; });
    sendingRef.current = true;
    seq.current += 1;
    setSending(true); setSendError(""); setLoadError("");
    let res = null;
    try {
      const { data: d } = await api.post("/sharing-tree/toy-followups/send", {
        year, household_ids: chosen.map((r) => r.household_id), batch_size: size, confirmed: true, resend, generations,
      });
      res = d;
    } catch (e) {
      if (mounted.current) setSendError(formatApiError(e));
    }
    sendingRef.current = false;
    if (!mounted.current) return;
    setSending(false);
    if (res) {
      setResult(res);
      setData(prev => prev ? { ...prev, usage: {
        date: res.date, daily_limit: res.daily_limit, daily_used: res.daily_used,
        daily_remaining: res.daily_remaining,
      } } : prev);
      setConfirmed(false);
      onComplete?.(res);
      load(res.remaining_ids || []);
    } else {
      load();
    }
  }

  const statusTone = (s) => s === "failed" ? "fb-status-error" : s === "sent" ? "fb-status-success" : s === "simulated" || s === "sending" ? "fb-status-warning" : "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4" style={{ background: "rgba(0,0,0,0.42)" }}
         onClick={() => { if (!sendingRef.current) onClose?.(); }}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="toy-followups-title" aria-busy={loading || sending}
           data-testid="toy-followups-dialog" onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl p-5 sm:p-7 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: "var(--fb-primary)" }}>
              <Gift className="w-4 h-4" aria-hidden="true" /> Sharing Tree {year}
            </div>
            <h2 id="toy-followups-title" className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Toy idea follow-up emails</h2>
            <p className="text-sm mt-1" style={{ color: "var(--fb-text-muted)" }}>
              A friendly note to registered households whose children (ages 0 to 9) do not have toy ideas yet. One email per household.
            </p>
          </div>
          <button ref={closeRef} type="button" onClick={onClose} disabled={sending} aria-label="Close"
                  className="text-stone-400 hover:text-stone-700 disabled:opacity-40"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4 text-sm">
          <button type="button" data-testid="toy-followups-refresh" onClick={() => load()} disabled={sending}
                  className="inline-flex items-center gap-1.5 font-semibold px-3 py-2 rounded-lg border hover:bg-stone-50 disabled:opacity-50" style={{ borderColor: "var(--fb-border)" }}>
            <RotateCcw className="w-4 h-4" aria-hidden="true" /> Refresh
          </button>
          {usage && <span className="tabular-nums" style={{ color: "#1D4ED8" }}>
            Shared daily email allowance: {usage.daily_remaining} of {usage.daily_limit} remaining ({usage.date})
          </span>}
          {data?.mock && <span className="fb-status-warning px-2 py-1 rounded text-xs font-semibold">Simulation mode: no real emails are delivered</span>}
        </div>

        {loading && !data && <div role="status" className="space-y-2" aria-label="Loading follow-up list">
          {[0, 1, 2].map((i) => <div key={i} className="h-9 rounded-lg animate-pulse" style={{ background: "var(--fb-bg)" }} />)}
        </div>}
        {loadError && <div role="alert" className="fb-status-error p-3 rounded-lg text-sm mb-3">
          Could not load the follow-up list: {loadError}{" "}
          <button type="button" className="underline font-semibold" onClick={() => load()} disabled={sending}>Try again</button>
        </div>}

        {data && <>
          {blocked && <div role="note" className="fb-status-warning p-3 rounded-lg text-sm mb-3">
            Sending is unavailable. {data.blocked_reason || "The toy idea window is not open."} This list is review only.
          </div>}

          {rows.length === 0 ? (
            <div className="py-10 text-center text-sm rounded-xl" style={{ background: "var(--fb-bg)", color: "var(--fb-text-muted)" }}>
              No registered households with missing toy requests were found for {year}.
            </div>
          ) : (
            <div className="max-h-72 overflow-auto rounded-xl border" style={{ borderColor: "var(--fb-border)" }} tabIndex={0} aria-label="Households to follow up">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="sticky top-0" style={{ background: "var(--fb-bg)" }}><tr style={{ color: "var(--fb-text-muted)" }}>
                  <th className={th}><input type="checkbox" aria-label="Select all available households" disabled={blocked || sending}
                    checked={rows.some(selectable) && rows.filter(selectable).every((r) => selected.has(r.household_id))}
                    onChange={(e) => selectAll(e.target.checked)} /></th>
                  <th className={th}>Household</th><th className={th}>Contact</th><th className={th}>Email</th>
                  <th className={th}>Children without ideas</th><th className={th}>Status</th>
                </tr></thead>
                <tbody>{rows.map((r) => {
                  const ok = selectable(r);
                  return <tr key={r.household_id} className="border-t" style={{ borderColor: "var(--fb-border)" }}>
                    <td className="px-3 py-2"><input type="checkbox" data-testid={`toy-followups-select-${r.household_id}`}
                      aria-label={`Select household ${r.household_label}`} disabled={!ok || blocked || sending}
                      checked={ok && selected.has(r.household_id)} onChange={() => toggle(r.household_id)} /></td>
                    <td className="px-3 py-2 font-mono text-xs">{r.household_label}</td>
                    <td className="px-3 py-2 font-medium">{r.contact_name || "Not shown"}</td>
                    <td className="px-3 py-2" style={{ overflowWrap: "anywhere" }}>
                      {r.email ? r.email : r.can_contact ? <span style={{ color: "var(--fb-text-muted)" }}>Hidden</span> : <span style={{ color: "var(--fb-text-muted)" }}>No email on file</span>}
                    </td>
                    <td className="px-3 py-2 tabular-nums">{r.missing_count}</td>
                    <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-xs font-semibold ${statusTone(r.delivery_status)}`}>
                      {STATUS_LABEL[r.delivery_status] || r.delivery_status}</span>
                      {r.last_sent_at && <div className="text-xs mt-0.5" style={{ color: "var(--fb-text-muted)" }}>{new Date(r.last_sent_at).toLocaleString()}</div>}
                    </td>
                  </tr>;
                })}</tbody>
              </table>
            </div>
          )}

          {data.template && <section className="mt-4 rounded-xl p-4 text-sm" style={{ background: "var(--fb-bg)", border: "1px solid var(--fb-border)" }} aria-label="Email preview">
            <div className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: "var(--fb-text-muted)" }}>
              Preview: each selected household receives this one email
            </div>
            <div className="font-semibold mb-2">Subject: {data.template.subject}</div>
            {(data.template.paragraphs || []).map((p, i) => <p key={i} className="mb-2">{p}</p>)}
            {data.template.portal_url && <a href={data.template.portal_url} target="_blank" rel="noopener noreferrer" className="underline font-semibold" style={{ color: "var(--fb-primary)" }}>Add toy ideas</a>}
            {data.close_date && <p className="mt-2 text-xs" style={{ color: "var(--fb-text-muted)" }}>Toy ideas accepted through {data.close_date}.</p>}
          </section>}

          <div className="mt-4 grid gap-3 sm:grid-cols-2 text-sm">
            <label className="flex items-start gap-2"><input type="checkbox" data-testid="toy-followups-resend" checked={resend}
              disabled={blocked || sending} onChange={(e) => { setResend(e.target.checked); setConfirmed(false); }} className="mt-1" />
              <span>Also include households that were already contacted (resend)</span></label>
            <label className="flex items-center gap-2">Emails in this batch
              <input type="number" min="1" max={maxBatch} value={batchSize} disabled={blocked || sending || remainingQuota === 0}
                onChange={(e) => { setBatchSize(e.target.value); setConfirmed(false); }} className="w-20 px-2 py-1.5 rounded-lg border" style={{ borderColor: "var(--fb-border)" }} /></label>
            <label className="flex items-start gap-2 sm:col-span-2"><input type="checkbox" data-testid="toy-followups-confirm" checked={confirmed}
              disabled={blocked || sending} onChange={(e) => setConfirmed(e.target.checked)} className="mt-1" />
              <span>I reviewed the preview and want to email {chosen.length} household{chosen.length !== 1 ? "s" : ""} ({children} child{children !== 1 ? "ren" : ""} without toy ideas). Up to {Math.min(chosen.length, Number(batchSize) || 1, maxBatch)} will be sent now.</span></label>
          </div>
          {remainingQuota === 0 && <p className="fb-status-warning p-2 rounded-lg text-sm mt-2">The shared daily email allowance is used up. Try again after it resets.</p>}
        </>}

        {sendError && <div role="alert" className="fb-status-error p-3 rounded-lg text-sm mt-3">Send failed: {sendError}</div>}
        {result && <div role="status" className="rounded-xl p-4 text-sm mt-3" style={{ background: "#ECFDF5", border: "1px solid #6EE7B7", color: "#065F46" }}>
          <strong>Batch results</strong>
          <div>{result.sent} sent{result.simulated ? `, ${result.simulated} simulated (not delivered)` : ""}, {result.skipped} skipped, {result.already_sent} already contacted, {result.in_progress} in progress elsewhere.</div>
          {result.failed?.length > 0 && <div>{result.failed.length} failed. Eligible households remain selected for retry.</div>}
          <div className="font-semibold">{result.remaining} pending from this selection. Allowance left today: {result.daily_remaining} of {result.daily_limit}.</div>
          {result.closed && <div>The toy idea window has closed; no further sends are possible.</div>}
        </div>}

        <div className="flex flex-col-reverse sm:flex-row gap-3 mt-5">
          <button type="button" onClick={onClose} disabled={sending} className="flex-1 px-4 py-2.5 rounded-xl border text-sm font-semibold disabled:opacity-50" style={{ borderColor: "var(--fb-border)" }}>Close</button>
          <button type="button" data-testid="toy-followups-send" onClick={send} disabled={!canSend}
                  className="flex-1 fb-btn-primary px-4 py-2.5 rounded-xl font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
            <Mail className="w-4 h-4" aria-hidden="true" />{sending ? "Sending..." : result ? "Send next batch" : "Send follow-ups"}
          </button>
        </div>
      </div>
    </div>
  );
}
